/**
 * §19 event contract. Every event carries the envelope; critical transaction events originate server-side
 * through the outbox. Payloads never contain contact details, tokens, seeds or evidence content.
 */
import { z } from 'zod';
import { HexHash256, IsoDateTime, LocaleTag, Ulid } from './common.js';
import { MoneyDto } from './money.js';
import { EngagementTaskType, EntrySource, FulfillmentType, LotteryMechanic, PaymentStatus, PrizeTier, SocialPlatform, VerificationDecision, WinnerContactStatus, } from './enums.js';
export const EVENT_SCHEMA_VERSION = 1;
/** Consent class the event was recorded under (§12.5 purposes are separate). */
export const ConsentClass = z.enum(['OPERATIONAL', 'ANALYTICS', 'SPONSOR', 'MARKETING']);
/** Sponsor placement identifiers (§7.1 placement map). */
export const SponsorPlacement = z.enum([
    'HERO_LOCKUP',
    'SPONSOR_STRIP',
    'PRIZE_BADGE',
    'CONFIRMATION_LINE',
    'COUNTDOWN',
    'INTERSTITIAL',
    'WHEEL_HUB',
    'SCRATCH_SKIN',
    'REVEAL_ACCENT',
    'WIN_CREDIT',
    'LOSS_MESSAGE',
    'SAMPLE_REVEAL',
    'CLAIM_CREDIT',
    'FOOTER_STRIP',
    'SHARE_CARD',
    'WIN_MESSAGE',
]);
export const SourcePlatform = z.union([SocialPlatform, z.literal('DIRECT')]);
/** Envelope shared by every topic. `subjectId` is a pseudonymous participant/profile id, never a contact. */
export const EventEnvelope = z.object({
    eventId: Ulid,
    schemaVersion: z.literal(EVENT_SCHEMA_VERSION),
    tenantId: Ulid,
    lotteryId: Ulid.optional(),
    configVersion: z.number().int().min(1).optional(),
    /** Pseudonymous subject (participant profile ULID, staff ULID or `anon:<hash>`); never a handle or number. */
    subjectId: z.string().min(1).max(80),
    serverTs: IsoDateTime,
    clientTs: IsoDateTime.optional(),
    sourcePlatform: SourcePlatform.optional(),
    locale: LocaleTag.optional(),
    consentClass: ConsentClass,
    placement: SponsorPlacement.optional(),
    /** Campaign / UTM tags (values only, never free text from the participant). */
    campaign: z.record(z.string().max(40), z.string().max(120)).optional(),
});
const outcomeKind = z.enum(['WIN', 'LOSS', 'SPONSOR_SAMPLE']);
/** Payload schema per topic. Keys are exactly `DomainEventTopic` values. */
export const EVENT_PAYLOADS = {
    lottery_viewed: z.object({
        slug: z.string(),
        status: z.string(),
        referrer: z.string().max(200).optional(),
    }),
    rules_expanded: z.object({ section: z.enum(['ODDS', 'RULES', 'PRIVACY', 'PRIZE', 'SPONSOR', 'FAQ']) }),
    entry_started: z.object({ mode: z.enum(['FREE', 'PAID']), quantity: z.number().int().min(1) }),
    entry_submitted: z.object({
        entryId: Ulid,
        mode: z.enum(['FREE', 'PAID']),
        quantity: z.number().int().min(1),
        source: EntrySource,
        paymentId: Ulid.optional(),
    }),
    payment_pending: z.object({ paymentId: Ulid, amount: MoneyDto, provider: z.string() }),
    entry_confirmed: z.object({
        entryId: Ulid,
        source: EntrySource,
        quantity: z.number().int().min(1),
        participantSequence: z.number().int().min(1).optional(),
        paymentId: Ulid.optional(),
    }),
    task_completed: z.object({
        taskId: Ulid,
        type: EngagementTaskType,
        status: z.enum(['PENDING', 'QUALIFIED', 'REJECTED']),
        bonusEntries: z.number().int().min(0),
        platform: SocialPlatform.optional(),
    }),
    reveal_presented: z.object({
        mechanic: LotteryMechanic,
        outcome: outcomeKind,
        energyLevel: z.enum(['FULL', 'CALM']),
        reducedMotion: z.boolean(),
        durationMs: z.number().int().min(0).optional(),
    }),
    reveal_skipped: z.object({
        mechanic: LotteryMechanic,
        outcome: outcomeKind,
        atMs: z.number().int().min(0),
    }),
    sponsor_sample_awarded: z.object({
        winId: z.string(),
        ruleId: Ulid,
        ruleVersion: z.number().int().min(1),
        sponsorId: Ulid,
        prizeId: Ulid,
        participantSequence: z.number().int().min(1),
    }),
    claim_started: z.object({ winId: z.string(), prizeId: Ulid, fulfillmentType: FulfillmentType }),
    variant_held: z.object({
        winId: z.string(),
        holdId: Ulid,
        variantId: Ulid,
        expiresAt: IsoDateTime,
        extension: z.boolean(),
    }),
    claim_submitted: z.object({
        winId: z.string(),
        claimId: Ulid,
        fulfillmentType: FulfillmentType,
        platform: SocialPlatform.optional(),
    }),
    verification_decided: z.object({
        claimId: Ulid,
        decision: VerificationDecision,
        /** Provider reason codes only (never free text with PII). */
        reasonCodes: z.array(z.string().max(64)).default([]),
        manualFallback: z.boolean().default(false),
    }),
    message_button_tapped: z.object({ winId: z.string(), platform: SocialPlatform }),
    winner_marked_messaged: z.object({
        winId: z.string(),
        status: WinnerContactStatus,
        platform: SocialPlatform.optional(),
        staffId: Ulid,
    }),
    fulfillment_completed: z.object({
        winId: z.string(),
        claimId: Ulid,
        fulfillmentType: FulfillmentType,
        evidenceRef: z.string().max(512),
        completedAt: IsoDateTime,
    }),
    sponsor_impression: z.object({
        sponsorId: Ulid,
        placement: SponsorPlacement,
        campaignId: z.string().max(80).optional(),
        viewable: z.boolean(),
        click: z.boolean().default(false),
    }),
    support_escalated: z.object({
        caseRef: z.string(),
        category: z.string().max(64),
        priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']),
        relatedWinId: z.string().optional(),
    }),
    // internal topics
    lottery_published: z.object({
        configVersion: z.number().int().min(1),
        seedCommit: HexHash256,
        mechanic: LotteryMechanic,
        publishedTermsHash: HexHash256,
    }),
    lottery_closed: z.object({
        closedAt: IsoDateTime,
        eligibleEntries: z.number().int().min(0),
        excludedEntries: z.number().int().min(0),
    }),
    draw_committed: z.object({ drawId: Ulid, seedCommit: HexHash256, reason: z.string().max(500).optional() }),
    draw_published: z.object({
        drawId: Ulid,
        snapshotHash: HexHash256,
        resultHash: HexHash256,
        winners: z.number().int().min(0),
        tiers: z.array(z.object({ tier: PrizeTier, prizeId: Ulid, winners: z.number().int().min(0) })),
    }),
    payment_succeeded: z.object({
        paymentId: Ulid,
        amount: MoneyDto,
        provider: z.string(),
        providerReference: z.string().max(128),
        entryIds: z.array(Ulid),
    }),
    payment_refunded: z.object({
        paymentId: Ulid,
        refundId: Ulid,
        amount: MoneyDto,
        partial: z.boolean(),
        resultingStatus: PaymentStatus,
        reason: z.string().max(64),
    }),
    hold_expired: z.object({
        holdId: Ulid,
        winId: z.string(),
        variantId: Ulid,
        releasedUnits: z.number().int().min(0),
    }),
    claim_expired: z.object({
        claimId: Ulid,
        winId: z.string(),
        policy: z.enum(['REDRAW', 'ROLLOVER', 'FORFEIT']),
    }),
    payout_approved: z.object({
        payoutId: Ulid,
        claimId: Ulid,
        amount: MoneyDto,
        approvers: z.array(Ulid).min(1),
        dualControl: z.boolean(),
    }),
};
const eventVariant = (topic) => EventEnvelope.extend({ topic: z.literal(topic), payload: EVENT_PAYLOADS[topic] });
/** Discriminated union of every event, for the outbox relay and analytics sinks. */
export const DomainEvent = z.discriminatedUnion('topic', [
    eventVariant('lottery_viewed'),
    eventVariant('rules_expanded'),
    eventVariant('entry_started'),
    eventVariant('entry_submitted'),
    eventVariant('payment_pending'),
    eventVariant('entry_confirmed'),
    eventVariant('task_completed'),
    eventVariant('reveal_presented'),
    eventVariant('reveal_skipped'),
    eventVariant('sponsor_sample_awarded'),
    eventVariant('claim_started'),
    eventVariant('variant_held'),
    eventVariant('claim_submitted'),
    eventVariant('verification_decided'),
    eventVariant('message_button_tapped'),
    eventVariant('winner_marked_messaged'),
    eventVariant('fulfillment_completed'),
    eventVariant('sponsor_impression'),
    eventVariant('support_escalated'),
    eventVariant('lottery_published'),
    eventVariant('lottery_closed'),
    eventVariant('draw_committed'),
    eventVariant('draw_published'),
    eventVariant('payment_succeeded'),
    eventVariant('payment_refunded'),
    eventVariant('hold_expired'),
    eventVariant('claim_expired'),
    eventVariant('payout_approved'),
]);
/** Topics a browser may emit through the analytics endpoint; everything else is server-originated (§19). */
export const CLIENT_EMITTABLE_TOPICS = [
    'lottery_viewed',
    'rules_expanded',
    'entry_started',
    'reveal_presented',
    'reveal_skipped',
    'sponsor_impression',
    'message_button_tapped',
];
/** Validate one event by its topic. */
export const parseDomainEvent = (input) => DomainEvent.parse(input);
/** Problem codes that analytics counts under the Integrity area (§19 "blocked attempts"). */
export const INTEGRITY_PROBLEM_CODES = [
    'rate_limited',
    'webhook_unauthenticated',
    'webhook_replayed',
    'idempotency_conflict',
    'tenant_mismatch',
    'win_link_invalid',
    'claim_token_invalid',
];
//# sourceMappingURL=events.js.map